import { useMemo, useState } from "react";
import { telLink, whatsappLink } from "@chbapark/domain/normalization";
import { Link } from "react-router-dom";
import { useAuth } from "../auth";
import { Avatar } from "../ui/Avatar";
import {
  INBOX_PAGE_SIZE,
  inboxBucket,
  inboxPriority,
  INBOX_TYPE_LABEL,
  primaryPhone,
  useCaptures,
  useInbox,
  useInboxTotal,
  useOpenServiceRequests,
  useOpenTasks,
  useRecentRegistrations,
  usePeopleByIds,
  usePeopleSearch,
  useNamedPeople,
  useTagDefs,
  type InboxItem,
  type WorkItem,
} from "../data";
import { CaptureBox } from "../ui/CaptureBox";
import { TagPicker } from "../ui/TagPicker";
import {
  attributeDonation,
  cancelTask,
  completeTask,
  resolveContactTriage,
  resolveInboxItem,
  resolveServiceRequest,
  snoozeTask,
} from "../actions";
import {
  IcAlert,
  IcCheck,
  IcChevron,
  IcForm,
  IcInbox,
  IcMerge,
  IcPeople,
  IcPhone,
  IcSearch,
  IcTent,
  IcUserPlus,
  IcWhatsapp,
} from "../ui/icons";

type Tab = "all" | "forms" | "attention" | "notes";

const SECTIONS = [
  {
    id: "forms" as const,
    title: "טפסים ופניות מהאתר",
    icon: <IcForm size={17} />,
    tint: "tint-green",
  },
  {
    id: "attention" as const,
    title: "תרומות וזיהוי",
    icon: <IcAlert size={17} />,
    tint: "tint-red",
  },
];

export function InboxPage() {
  const { user } = useAuth();
  const [pageSize, setPageSize] = useState(INBOX_PAGE_SIZE);
  const [tab, setTab] = useState<Tab>("all");
  const [searching, setSearching] = useState(false);
  const [term, setTerm] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const { data: inboxLive, loading } = useInbox(pageSize);

  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const hide = (id: string) => setHidden((s) => new Set(s).add(id));
  const unhide = (id: string) =>
    setHidden((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
  const data = useMemo(() => inboxLive.filter((i) => !hidden.has(i.id)), [inboxLive, hidden]);
  const total = useInboxTotal();
  const { data: captures } = useCaptures();

  const tasks = useOpenTasks(pageSize, ["intake.thank"]);
  const registrations = useRecentRegistrations(pageSize);
  const serviceRequests = useOpenServiceRequests(pageSize);

  const workRaw = useMemo(
    () =>
      [...tasks.items, ...registrations.items, ...serviceRequests.items].sort(
        (a, b) =>
          Number(a.kind === "registration") - Number(b.kind === "registration") ||
          (b.at ?? "").localeCompare(a.at ?? ""),
      ),
    [tasks.items, registrations.items, serviceRequests.items],
  );

  const workPeople = useNamedPeople(
    workRaw.map((w) => w.personId).filter((id): id is string => Boolean(id)),
  );
  const work = useMemo(() => {
    const byId = new Map(workPeople.data.map((p) => [p.id, p]));
    return workRaw.map((w) => {
      const p = w.personId ? byId.get(w.personId) : undefined;
      const name = p?.displayName ?? undefined;
      const phone = p ? primaryPhone(p) : null;
      return {
        ...w,
        personName: name ?? null,
        personPhone: phone,
        lines: name && !w.lines.includes(name) ? [name, ...w.lines] : w.lines,
      };
    });
  }, [workRaw, workPeople.data]);

  const counts = useMemo(() => {
    const attentionCount = data.length;
    return {
      all: attentionCount + work.length + captures.length,
      notes: captures.length,
      forms: work.length,
      attention: attentionCount,
    };
  }, [data.length, work.length, captures.length]);

  const q = term.trim().toLowerCase();
  const matchesItem = (i: InboxItem) =>
    q === "" ||
    [i.personName, i.personPhone, i.title, i.sourceText, i.evidence?.fullName, i.evidence?.note]
      .concat(i.evidence?.phones ?? [], i.evidence?.emails ?? [])
      .some((v) => typeof v === "string" && v.toLowerCase().includes(q));
  const matchesWork = (w: WorkItem) =>
    q === "" || [w.title, w.personPhone ?? "", ...w.lines].some((v) => v.toLowerCase().includes(q));

  const byPriority = (list: InboxItem[]) =>
    [...list].sort((a, b) => inboxPriority(a.type) - inboxPriority(b.type));

  const shown =
    tab === "all" || tab === "attention"
      ? byPriority(data.filter(matchesItem))
      : [];

  const shownWork = work.filter(matchesWork);

  const showWork = tab === "all" || tab === "forms";

  const showCaptures = tab === "all" || tab === "notes";
  const matchingCaptures = captures.filter(
    (c) => q === "" || (c.text ?? "").toLowerCase().includes(q),
  );

  const capped = tab === "all" && total !== null && total > data.length && q === "";

  async function act(item: InboxItem, resolution: string) {
    setBusy(item.id);
    setNote(null);
    hide(item.id);
    try {
      await resolveInboxItem(item.id, resolution);
    } catch {
      unhide(item.id);
      setNote("לא הצלחנו לשמור, אז החזרנו את זה לרשימה. נסו שוב.");
    } finally {
      setBusy(null);
    }
  }

  async function addContact(item: InboxItem, tagIds: string[], note = "") {
    setBusy(item.id);
    setNote(null);
    hide(item.id);
    try {
      const r = await resolveContactTriage(item.id, "add", tagIds, note);
      if (r.status === "linked") setNote("איש הקשר כבר היה במערכת. עדכנו לו את התגיות.");
      else if (r.status === "ambiguous") {
        unhide(item.id);
        setNote("הטלפון והמייל שייכים לשני אנשי קשר שונים. השארנו את הכרטיס פתוח כדי שתחליטו.");
      }
    } catch {
      unhide(item.id);
      setNote("לא הצלחנו לשמור, אז החזרנו את זה לרשימה. נסו שוב.");
    } finally {
      setBusy(null);
    }
  }

  async function dismissContact(item: InboxItem) {
    setBusy(item.id);
    setNote(null);
    hide(item.id);
    try {
      await resolveContactTriage(item.id, "dismiss");
    } catch {
      unhide(item.id);
      setNote("לא הצלחנו לשמור, אז החזרנו את זה לרשימה. נסו שוב.");
    } finally {
      setBusy(null);
    }
  }

  const TABS: { id: Tab; label: string; n: number }[] = [
    { id: "all", label: "הכול", n: (total ?? data.length) + work.length + captures.length },
    { id: "forms", label: "טפסים ופניות", n: counts.forms },
    { id: "attention", label: "תרומות וזיהוי", n: total ?? counts.attention },
    { id: "notes", label: "פתקים שלי", n: counts.notes },
  ];

  return (
    <div className="screen">
      <header className="hdr">
        <div className="hdr-top">
          <div className="avatar-me">{(user?.displayName ?? "מ").charAt(0)}</div>
          <div className="hdr-titles">
            <h1>
              <IcInbox size={20} /> אינבוקס
            </h1>
            {/* Both halves named, because the screen now holds both: notes
                somebody wrote, and decisions the system would not guess. */}
            <p className="sub">פתקים שכתבתם, ודברים שמחכים לטיפול</p>
          </div>
          {/*
            One button, and it works.

            There were three: filter, search and a bell. None did anything — each
            took the pressed state and then nothing happened, which is worse than
            an absent control because it teaches the reader that taps do not
            work here. The tabs below already filter, and the bell pointed at the
            Inbox from inside the Inbox.
          */}
          <div className="hdr-actions">
            <button
              className="icon-btn"
              aria-label="חיפוש"
              aria-pressed={searching}
              onClick={() => {
                setSearching((v) => !v);
                if (searching) setTerm("");
              }}
            >
              <IcSearch />
            </button>
          </div>
        </div>

        {searching && (
          <div className="searchbar">
            <IcSearch size={18} />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="חיפוש לפי שם, טלפון או תוכן…"
              autoFocus
              autoComplete="off"
            />
          </div>
        )}

        <div className="pilltabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className={tab === t.id ? "on" : undefined}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              <span className="tab-count">{t.n}</span>
            </button>
          ))}
        </div>
      </header>

      <div className="body">
        {note && (
          <p className="conn-note" style={{ margin: "12px 0 0" }}>
            {note}
          </p>
        )}

        {capped && (
          <p className="inbox-truncated">
            מוצגים {data.length} מתוך <strong>{total}</strong> שמחכים לטיפול.
          </p>
        )}
        {q !== "" && (
          <p className="inbox-truncated">
            {shown.length + shownWork.length} תוצאות ל„{term}”. חיפשנו רק במה שכבר נטען למסך.
          </p>
        )}

        {showCaptures && <CaptureBox captures={matchingCaptures} />}

        {/* Forms and registrations, from the collections that hold them. */}
        {showWork && shownWork.length > 0 && (
          <section className="section">
            <div className="section-head">
              <div className="section-ico tint-green">
                <IcForm size={17} />
              </div>
              <div className="section-title">
                טפסים ופניות מהאתר <span className="section-n">{shownWork.length}</span>
              </div>
              {tab === "all" && shownWork.length > 5 && (
                <button className="see-all warm" onClick={() => setTab("forms")}>
                  לכל הרשימה <IcChevron size={14} />
                </button>
              )}
            </div>
            <div className="card">
              {(tab === "all" ? shownWork.slice(0, 5) : shownWork).map((w, i, arr) => (
                <WorkRow key={`${w.kind}_${w.id}`} item={w} last={i === arr.length - 1} />
              ))}
            </div>
          </section>
        )}

        {loading ? (
          <p className="muted" style={{ padding: 22, textAlign: "center" }}>
            טוענים…
          </p>
        ) : shown.length === 0 &&
          !(showWork && shownWork.length > 0) &&
          !(showCaptures && matchingCaptures.length > 0) ? (
          tab === "notes" ? null : (
            <EmptyInbox all={counts.all === 0} />
          )
        ) : tab === "all" ? (
          SECTIONS.map((s) => {
            if (s.id === "forms") return null;
            const items = byPriority(data);
            if (!items.length) return null;
            return (
              <section className="section" key={s.id}>
                <div className="section-head">
                  <div className={`section-ico ${s.tint}`}>
                    {s.icon}
                  </div>
                  <div className="section-title">
                    {s.title} <span className="section-n">{items.length}</span>
                  </div>
                  {items.length > 5 && (
                    <button className="see-all warm" onClick={() => setTab(s.id)}>
                      לכל הרשימה <IcChevron size={14} />
                    </button>
                  )}
                </div>
                {items.slice(0, 5).map((item) => (
                  <Card
                    key={item.id}
                    item={item}
                    busy={busy === item.id}
                    onAct={act}
                    onAdd={addContact}
                    onDismiss={dismissContact}
                  />
                ))}
              </section>
            );
          })
        ) : (
          <>
            {shown.map((item) => (
              <Card
                key={item.id}
                item={item}
                busy={busy === item.id}
                onAct={act}
                onAdd={addContact}
                onDismiss={dismissContact}
              />
            ))}

            {shown.length >= pageSize && (
              <button
                className="btn btn-wide"
                style={{ margin: "4px 0 18px" }}
                onClick={() => setPageSize((n) => n + INBOX_PAGE_SIZE)}
              >
                הציגו עוד {INBOX_PAGE_SIZE}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const WORK_STYLE: Record<WorkItem["kind"], { label: string; tint: string; icon: React.ReactNode }> = {
  task: { label: "פנייה מהאתר", tint: "tint-orange", icon: <IcForm size={17} /> },
  registration: { label: "הרשמה", tint: "tint-green", icon: <IcTent size={17} /> },
  service_request: { label: "בקשת שירות", tint: "tint-slate", icon: <IcAlert size={17} /> },
};

function inAWeek(): string {
  return new Date(Date.now() + 7 * 86_400_000).toISOString();
}

function WorkRow({ item, last }: { item: WorkItem; last: boolean }) {
  const s = WORK_STYLE[item.kind];
  const when = item.at
    ? new Date(item.at).toLocaleDateString("he-IL", { day: "numeric", month: "long" })
    : null;
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [cleared, setCleared] = useState(false);

  const waUrl = item.personPhone ? whatsappLink(item.personPhone) : null;
  const callUrl = item.personPhone ? telLink(item.personPhone) : null;

  const body = (
    <>
      <span className={`rail-ico ${s.tint}`}>
        {s.icon}
      </span>
      <span className="grow">
        <span className="name-row">
          {item.title}
        </span>
        {item.lines.slice(0, 2).map((l, i) => (
          <span className="muted" key={i} style={{ display: "block" }}>
            {l}
          </span>
        ))}
        <span className="faint" style={{ display: "block", marginTop: 2 }}>
          {s.label}
          {when && ` · ${when}`}
          {item.personPhone && ` · ${item.personPhone}`}
          {item.overdue && " · באיחור"}
        </span>
      </span>
      {item.personId && <IcChevron size={16} />}
    </>
  );

  const bottom = last ? "0" : undefined;

  const row = item.personId ? (
    <Link to={`/people/${item.personId}`} className="list-row" style={{ borderBottom: 0 }}>
      {body}
    </Link>
  ) : (
    <div className="list-row" style={{ borderBottom: 0 }}>
      {body}
    </div>
  );

  if (item.kind === "registration") {
    return (
      <div className="work-item" style={{ borderBottom: bottom }}>
        {row}
        {(waUrl || callUrl) && (
          <div className="row-actions">
            {waUrl && (
              <a className="btn btn-sm" href={waUrl} target="_blank" rel="noreferrer">
                <IcWhatsapp size={14} /> וואטסאפ
              </a>
            )}
            {callUrl && (
              <a className="btn btn-sm" href={callUrl}>
                <IcPhone size={14} /> שיחה
              </a>
            )}
          </div>
        )}
      </div>
    );
  }

  if (cleared) return null;

  async function run(which: string, fn: () => Promise<void>) {
    setBusy(which);
    setFailed(false);
    setCleared(true);
    try {
      await fn();
    } catch {
      setCleared(false);
      setFailed(true);
    } finally {
      setBusy(null);
    }
  }

  const isTask = item.kind === "task";
  const noun = isTask ? "המשימה" : "הבקשה";

  return (
    <div className="work-item" style={{ borderBottom: bottom }}>
      {row}
      <div className="row-actions">
        <button
          className="btn btn-primary btn-sm"
          disabled={busy !== null}
          onClick={() =>
            run("done", () =>
              isTask ? completeTask(item.id) : resolveServiceRequest(item.id, "done"),
            )
          }
        >
          <IcCheck size={14} /> {isTask ? "בוצע" : "טופל"}
        </button>

        {waUrl && (
          <a className="btn btn-sm" href={waUrl} target="_blank" rel="noreferrer">
            <IcWhatsapp size={14} /> וואטסאפ
          </a>
        )}

        {callUrl && (
          <a className="btn btn-sm" href={callUrl}>
            <IcPhone size={14} /> שיחה
          </a>
        )}

        {isTask && (
          <button
            className="btn btn-sm"
            disabled={busy !== null}
            onClick={() => run("snooze", () => snoozeTask(item.id, inAWeek()))}
          >
            דחו בשבוע
          </button>
        )}

        <button
          className="btn btn-sm danger"
          disabled={busy !== null}
          onClick={() => {
            if (!confirm(`למחוק את ${noun} „${item.title}”? היא תיעלם מהרשימה, אבל תישאר בהיסטוריה.`))
              return;
            void run("cancel", () =>
              isTask
                ? cancelTask(item.id, "נמחקה מהאינבוקס")
                : resolveServiceRequest(item.id, "cancelled", "נמחקה מהאינבוקס"),
            );
          }}
        >
          מחיקה
        </button>
      </div>
      {failed && (
        <p className="faint danger" style={{ padding: "0 14px 12px", margin: 0 }}>
          לא הצלחנו לשמור, אז החזרנו את זה לרשימה. נסו שוב.
        </p>
      )}
    </div>
  );
}

function Card({
  item,
  busy,
  onAct,
  onAdd,
  onDismiss,
}: {
  item: InboxItem;
  busy: boolean;
  onAct: (i: InboxItem, r: string) => void;
  onAdd: (i: InboxItem, tagIds: string[], note: string) => void;
  onDismiss: (i: InboxItem) => void;
}) {
  if (item.type === "new_contact") return <NewContactCard item={item} busy={busy} onAct={onAct} />;
  if (item.type === "contact_to_triage")
    return <ImportedContactCard item={item} busy={busy} onAdd={onAdd} onDismiss={onDismiss} />;
  if (item.type === "unattributed_payment")
    return <UnattributedPaymentCard item={item} busy={busy} onAct={onAct} />;
  if (item.type === "registration") return <RegistrationCard item={item} busy={busy} onAct={onAct} />;
  if (item.type === "unhandled_submission")
    return <UnhandledSubmissionCard item={item} busy={busy} onAct={onAct} />;
  /*
   * The fall-through is the duplicate card, and that is now a deliberate choice
   * rather than an accident. `possible_duplicate` is the only remaining type the
   * server creates, and an unknown type rendered as a duplicate would at least
   * show its title and its two buttons.
   *
   * It used to catch `unhandled_submission` as well — a submission the CRM could
   * not file, drawn as a question about two people who do not exist. The whole
   * point of that item is that the payload is attached, and the card threw it
   * away.
   */
  return <DuplicateCard item={item} busy={busy} onAct={onAct} />;
}

/** Formats Google's last-modified date, which is the only age signal it gives. */
function formatSourceDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("he-IL", { month: "long", year: "numeric" }).format(d);
}

/**
 * A contact pulled from a Google address book.
 *
 * Shown separately from `new_contact` because the question is different. A form
 * submission is somebody who just reached out; an imported contact may be a
 * plumber from 2019. So the card leads with the evidence — every number, every
 * address, the Google labels, and the date Google last touched the record — and
 * only then asks. Without that date the reviewer has no way to tell a neighbour
 * from a one-off tradesman.
 *
 * ## Two actions, and only one of them creates anything
 *
 * **הוסף** creates the person with whichever tags are selected. **לא רלוונטי**
 * creates nothing and closes the card permanently.
 *
 * The tags are the point of the screen. Adding somebody with no tag beyond the
 * baseline records that they exist and nothing about who they are — which is
 * most of the value. So the chips sit above the button, pre-filtered to the
 * vocabulary the product owner maintains in Settings, and any Google label is
 * shown right next to them as a hint about which to pick.
 */
function ImportedContactCard({
  item,
  busy,
  onAdd,
  onDismiss,
}: {
  item: InboxItem;
  busy: boolean;
  onAdd: (i: InboxItem, tagIds: string[], note: string) => void;
  onDismiss: (i: InboxItem) => void;
}) {
  const e = item.evidence ?? {};
  const name = e.fullName ?? "ללא שם";
  const when = formatSourceDate(e.sourceUpdatedAt);
  const [selected, setSelected] = useState<string[]>([]);
  const [noteText, setNoteText] = useState("");

  return (
    <article className="card">
      <div className="card-body row">
        <Avatar name={name} size={48} />
        <div className="grow">
          <p className="name">{name}</p>
          {(e.phones ?? []).map((p) => (
            <p className="muted mono" key={p} dir="ltr" style={{ textAlign: "right" }}>
              {p}
            </p>
          ))}
          {(e.emails ?? []).map((m) => (
            <p className="muted mono" key={m} dir="ltr" style={{ textAlign: "right" }}>
              {m}
            </p>
          ))}
          {e.organization && <p className="muted">{e.organization}</p>}
          {e.note && <p className="faint">{e.note}</p>}

          {/* The label the product owner already put on this contact in Google.
              Where it exists it usually answers "who is this?" faster than
              anything else on the card. */}
          {(e.labels ?? []).length > 0 && (
            <p className="gc-labels">
              {(e.labels ?? []).map((l) => (
                <span className="gc-label" key={l}>
                  {l}
                </span>
              ))}
            </p>
          )}

          <p className="faint">מאנשי הקשר בגוגל{when ? ` · עודכן ${when}` : ""}</p>
        </div>
        <div className="rail-ico tint-slate">
          <IcPeople />
        </div>
      </div>

      <div className="ask">
        <p className="ask-q">מי זה?</p>

        {/*
          A sentence, before the chips.

          This screen asks "who is this?" and the only answer it accepted was a
          tag from a fixed list. The product owner asked for somewhere to write a
          few words about the person and how they know them — and that is the
          better answer: "אבא של איתמר מהגן, פגשתי בהתוועדות" says more than any
          chip, and it is what somebody actually remembers while looking at the
          name.
        */}
        <textarea
          className="triage-note"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="כמה מילים: מי זה ומאיפה אתם מכירים…"
          rows={2}
          disabled={busy}
        />

        {/*
          Eight chips, not thirty-nine.

          Every person tag was rendered on every card — the product owner counted
          ten lines of them and said, correctly, that this should take two
          seconds. A list that long is not a choice, it is a reading task, and it
          is in the way of the decision the card exists for.

          The ones already used most often come first, because tagging is
          repetitive by nature; the rest are one tap away, and typing filters the
          whole vocabulary. Nothing was removed — only the wall.
        */}
        <TagPicker
          selected={selected}
          busy={busy}
          onToggle={(id, on) =>
            setSelected((prev) => (on ? [...new Set([...prev, id])] : prev.filter((t) => t !== id)))
          }
        />
        <div style={{ height: 10 }} />

        <div className="btn-row">
          <button className="btn" disabled={busy} onClick={() => onDismiss(item)}>
            לא רלוונטי
          </button>
          <button
            className="btn btn-primary"
            disabled={busy}
            onClick={() => onAdd(item, selected, noteText)}
          >
            {busy ? "שומרים…" : selected.length > 0 ? `הוסיפו · ${selected.length}` : "הוסיפו"}
          </button>
        </div>

        {/* The way out of a queue, offered where the queue is felt. */}
        <Link to="/triage" className="faint" style={{ display: "block", marginTop: 10 }}>
          הרבה ממתינים? סננו ומחקו כמה ביחד ←
        </Link>
      </div>
    </article>
  );
}

/**
 * A payment that was collected but could not be attributed to anybody.
 *
 * Leads with the amount, because that is what makes this urgent: the money is
 * already in the account and the CRM cannot say whose it is. The reasons are
 * shown in plain Hebrew rather than as codes, since "why can't you tell?" is the
 * first thing a reviewer asks.
 */
function UnattributedPaymentCard({
  item,
  busy,
  onAct,
}: {
  item: InboxItem;
  busy: boolean;
  onAct: (i: InboxItem, r: string) => void;
}) {
  const e = item.evidence ?? {};
  const shekels =
    typeof e.amountMinor === "number" ? (e.amountMinor / 100).toLocaleString("he-IL") : null;

  const [searchTerm, setSearchTerm] = useState(e.payerName ?? "");
  const [attrBusy, setAttrBusy] = useState(false);
  const [attrErr, setAttrErr] = useState<string | null>(null);

  const candidates = usePeopleByIds(item.candidatePersonIds);
  const search = usePeopleSearch(searchTerm);

  const matches = useMemo(() => {
    const seen = new Set<string>();
    const out = [];
    for (const p of [...candidates.data, ...search.data]) {
      if (!p?.id || seen.has(p.id) || p.status === "deleted") continue;
      seen.add(p.id);
      out.push(p);
      if (out.length >= 6) break;
    }
    return out;
  }, [candidates.data, search.data]);

  const REASON_TEXT: Record<string, string> = {
    no_contact_identifier: "לא הגיעו טלפון או מייל, ולפי שם בלבד אנחנו לא מזהים",
    amount_unreadable: "לא הצלחנו לקרוא את הסכום שהתקבל",
    recurring_not_yet_modelled: "זה חיוב של הוראת קבע, ועוד אין אצלנו ניהול הוראות קבע",
    conflicting_phone_email_mapping: "הטלפון והמייל שייכים לשני אנשי קשר שונים",
  };

  async function handleAttribute(personId: string) {
    setAttrBusy(true);
    setAttrErr(null);
    try {
      if (e.donationId) {
        await attributeDonation(e.donationId, personId);
      }
      onAct(item, "attributed");
    } catch (err) {
      setAttrErr(err instanceof Error ? err.message : "לא הצלחנו לשייך את התרומה. נסו שוב.");
      setAttrBusy(false);
    }
  }

  const isBusy = busy || attrBusy;

  return (
    <article className="card">
      <div className="card-body row">
        <div
          className="avatar avatar-lg tint-amber"
          aria-hidden="true"
        >
          ₪
        </div>
        <div className="grow">
          <p className="name">
            {shekels ? `תרומה של ${shekels} ₪ ללא שיוך` : "תרומה ללא שיוך"}
          </p>
          {e.payerName && <p className="muted">שם המשלם: {e.payerName}</p>}
          {e.purpose && <p className="muted">{e.purpose}</p>}
          <p className="faint">
            {e.collectedAt ? `נגבה ב-${e.collectedAt}` : "התקבל מ-Grow"}
            {e.providerDocumentId ? ` · אסמכתא ${e.providerDocumentId}` : ""}
          </p>
        </div>
        <div className="rail-ico tint-amber">
          <IcAlert />
        </div>
      </div>

      <div className="alert">
        <div className="alert-top">
          <span className="alert-txt">
            <strong>הכסף נרשם, אבל לא ידענו של מי הוא</strong>
            {(e.reasons ?? []).map((r) => REASON_TEXT[r] ?? r).join(". ")}
          </span>
        </div>
      </div>

      <div className="ask">
        <p className="ask-q">בחרו או חפשו איש קשר לשיוך התרומה:</p>

        <div className="search-wrap" style={{ marginBottom: 10 }}>
          <span className="search-ico" aria-hidden="true">
            <IcSearch size={16} />
          </span>
          <input
            className="search"
            value={searchTerm}
            onChange={(ev) => setSearchTerm(ev.target.value)}
            placeholder="חפשו תורם לפי שם או טלפון…"
            disabled={isBusy}
            autoComplete="off"
          />
          {searchTerm && (
            <button
              type="button"
              className="search-clear"
              onClick={() => setSearchTerm("")}
              disabled={isBusy}
              aria-label="ניקוי חיפוש"
            >
              ×
            </button>
          )}
        </div>

        {matches.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
            {matches.map((p, idx) => {
              const phone = primaryPhone(p);
              return (
                <div
                  key={p.id}
                  className="row"
                  style={{
                    padding: "8px 10px",
                    border: "1px solid var(--line)",
                    background: "var(--surface)",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <div className="grow">
                    <Link to={`/people/${p.id}`} className="name">
                      {p.displayName ?? "ללא שם"}
                    </Link>
                    {phone && (
                      <p className="faint mono" dir="ltr" style={{ margin: 0 }}>
                        {phone}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    className={idx === 0 ? "btn btn-primary" : "btn"}
                    disabled={isBusy}
                    onClick={() => void handleAttribute(p.id)}
                  >
                    {attrBusy ? "משייכים…" : `שייך ל־${p.displayName ?? "איש קשר"}`}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {searchTerm.trim().length >= 2 && !search.loading && matches.length === 0 && (
          <p className="faint" style={{ margin: "0 0 10px" }}>
            לא מצאנו איש קשר בשם „{searchTerm.trim()}”. נסו לחפש לפי שם משפחה או מספר טלפון.
          </p>
        )}

        {attrErr && (
          <p className="faint danger" style={{ margin: "0 0 10px" }}>
            {attrErr}
          </p>
        )}

        <div className="btn-row">
          <button className="btn" disabled={isBusy} onClick={() => onAct(item, "dismissed")}>
            סמנו שטופלה ללא שיוך
          </button>
        </div>
      </div>
    </article>
  );
}

/** New contact — asks how to classify, exactly as in the mockup. */
function NewContactCard({
  item,
  busy,
  onAct,
}: {
  item: InboxItem;
  busy: boolean;
  onAct: (i: InboxItem, r: string) => void;
}) {
  const name = item.personName ?? "ללא שם";
  // RTL order: avatar first (renders right), overflow menu last (renders left).
  return (
    <article className="card">
      <div className="card-body row">
        <Avatar name={name} size={48} />
        <div className="grow">
          <p className="name">{name}</p>
          {item.personPhone && <p className="muted mono">{item.personPhone}</p>}
          <p className="faint">{item.sourceText}</p>
        </div>
        <div
          className="rail-ico tint-orange"
        >
          <IcUserPlus />
        </div>
      </div>

      <div className="ask">
        <p className="ask-q">איך לסווג את איש הקשר?</p>
        <div className="btn-row">
          <button
            className="btn btn-primary"
            disabled={busy}
            onClick={() => onAct(item, "community_member")}
          >
            מהקהילה
          </button>
          <button className="btn" disabled={busy} onClick={() => onAct(item, "business")}>
            ספק / עסק
          </button>
          <button className="btn" disabled={busy} onClick={() => onAct(item, "irrelevant")}>
            לא רלוונטי
          </button>
        </div>
      </div>
    </article>
  );
}

/** Registration — shows the form, and if the processor suspected a duplicate,
 *  the decision is offered right here rather than on another screen. */
function RegistrationCard({
  item,
  busy,
  onAct,
}: {
  item: InboxItem;
  busy: boolean;
  onAct: (i: InboxItem, r: string) => void;
}) {
  return (
    <article className="card">
      <div className="card-body row">
        <div
          className="avatar"
          style={{
            background: "var(--green-soft)",
            color: "var(--green)",
            width: 48,
            height: 48,
          }}
          aria-hidden="true"
        >
          <IcTent size={22} />
        </div>
        <div className="grow">
          <p className="name">
            {item.title}
          </p>
          {(item.detailLines ?? []).map((l, i) => (
            <p className="muted" key={i}>
              {l}
            </p>
          ))}
          <p className="faint">{item.receivedText}</p>
        </div>
        <div
          className="rail-ico tint-orange"
        >
          <IcForm />
        </div>
      </div>

      {item.duplicateOf && (
        <div className="alert">
          <div className="alert-top">
            <span style={{ color: "var(--amber)", flexShrink: 0 }}>
              <IcPeople size={18} />
            </span>
            <span className="alert-txt">
              <strong>יש במערכת איש קשר עם פרטים דומים</strong>
              {item.duplicateOf.name}
              {item.duplicateOf.phone && (
                <>
                  {" "}
                  (<span className="mono">{item.duplicateOf.phone}</span>)
                </>
              )}
            </span>
            <IcChevron size={16} />
          </div>
          <div className="btn-row">
            <button
              className="btn btn-primary"
              disabled={busy}
              onClick={() => onAct(item, "same_person")}
            >
              זה אותו אחד
            </button>
            <button className="btn" disabled={busy} onClick={() => onAct(item, "different_person")}>
              זה מישהו אחר
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

/**
 * A form from the website the CRM did not know how to file.
 *
 * The processor creates this instead of marking a submission handled when
 * nothing actionable came out of it — a matzah order, some new form GPT Codex
 * put on the site before the CRM learned about it. Silently marking it processed
 * would mean the website got its 202 and the enquiry is gone, which is the
 * failure this item exists to make visible.
 *
 * So the card shows **the payload**. Not a summary of it: the actual fields the
 * person filled in, because the only way to handle one of these is by hand and
 * the only way to handle it by hand is to see what they wrote. Until now this
 * type had no card at all and fell through to the duplicate renderer, which
 * printed a question mark and two buttons about two people who did not exist.
 */
function UnhandledSubmissionCard({
  item,
  busy,
  onAct,
}: {
  item: InboxItem;
  busy: boolean;
  onAct: (i: InboxItem, r: string) => void;
}) {
  const [showPayload, setShowPayload] = useState(false);
  const payload = item.evidence?.payload;

  /** The payload flattened to label-and-value rows. A nested object is shown as
   *  JSON rather than as "[object Object]", which is what a naive render gives
   *  and is worse than showing nothing. */
  const fields: [string, string][] =
    payload && typeof payload === "object"
      ? Object.entries(payload as Record<string, unknown>)
          .filter(([, v]) => v !== null && v !== undefined && v !== "")
          .map(([k, v]) => [k, typeof v === "object" ? JSON.stringify(v) : String(v)])
      : [];

  return (
    <article className="card">
      <div className="card-body row">
        <span
          className="rail-ico tint-red"
        >
          <IcForm size={17} />
        </span>
        <div className="grow">
          <p className="name">
            {item.title ?? "פנייה מהאתר שלא ידענו לשייך"}
          </p>
          <p className="muted">
            שמרנו את הטופס במלואו. אין לו פעולה אוטומטית, אז צריך לטפל בו ידנית.
          </p>
          {(item.detailLines ?? []).map((line) => (
            <p className="faint" key={line} style={{ margin: "2px 0 0" }}>
              {line}
            </p>
          ))}
        </div>
      </div>

      {fields.length > 0 && (
        <div style={{ padding: "0 14px 8px" }}>
          <button className="see-all warm" onClick={() => setShowPayload((v) => !v)}>
            {showPayload ? "הסתירו את מה שנכתב" : `הציגו את מה שנכתב (${fields.length})`}
          </button>
          {showPayload && (
            <div style={{ marginTop: 8 }}>
              {fields.map(([k, v]) => (
                <div key={k} className="row" style={{ gap: 8, padding: "4px 0" }}>
                  <span className="faint faint-xs mono" style={{ flexShrink: 0 }}>
                    {k}
                  </span>
                  <span className="t-md" style={{ wordBreak: "break-word" }}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="btn-row card-foot">
        {/*
          One action, and it is honest about what it does. "טופל" closes the
          item; it does not file anything, because nothing here can be filed
          automatically — that is the definition of this type.
        */}
        <button className="btn" disabled={busy} onClick={() => onAct(item, "handled")}>
          <IcCheck size={15} /> {busy ? "שומרים…" : "טופל"}
        </button>
      </div>
    </article>
  );
}

/** Plain Hebrew for why the processor stopped and asked. */
const DUPLICATE_REASON: Record<string, string> = {
  conflicting_phone_email_mapping:
    "בטופס שהתקבל, הטלפון שייך לאיש קשר אחד והמייל לאיש קשר אחר.",
  no_contact_identifier: "לא הגיע טלפון או מייל שאפשר לסמוך עליו, ולפי שם בלבד אנחנו לא מזהים.",
  multiple_matches: "יותר מכרטיס אחד מתאים לפרטים שהתקבלו.",
};

/**
 * Possible duplicate — the case the processor deliberately refused to guess.
 *
 * It used to refuse to tell you anything either. The card read "מצאנו 2 כרטיסים
 * אפשריים עבור:" and then stopped, because it printed `item.personName` and a
 * `possible_duplicate` has no such field: what it carries is
 * `candidatePersonIds` and an `evidence.reason`. So the one screen in the CRM
 * whose entire job is a judgement call showed a question mark, a question, and
 * two buttons — and never named either person. Reported as "what is this thing?",
 * which was the only possible reaction.
 *
 * The two candidates are now fetched and shown with their phone and email, each
 * one openable, and the reason is spelled out. Deciding this without seeing both
 * records is not a decision, it is a coin toss on somebody's identity.
 */
function DuplicateCard({
  item,
  busy,
  onAct,
}: {
  item: InboxItem;
  busy: boolean;
  onAct: (i: InboxItem, r: string) => void;
}) {
  const ids = item.candidatePersonIds ?? [];
  const { data: candidates, loading } = usePeopleByIds(ids);
  const reason = item.evidence?.reason ?? null;

  return (
    <article className="card">
      <div className="card-body row">
        <div
          className="avatar avatar-lg tint-red"
        >
          ?
        </div>
        <div className="grow">
          <p className="name">
            {/* Named by its type when it has no title of its own. This card is
                the fall-through for anything unrecognised, and an unknown item
                that announced itself as a possible duplicate was a claim the
                screen had no basis for. */}
            {item.title ?? INBOX_TYPE_LABEL[item.type ?? ""] ?? "לא זיהינו מה זה"}
          </p>
          <p className="muted">
            {reason ? (DUPLICATE_REASON[reason] ?? reason) : `מצאנו ${ids.length || 2} כרטיסים שאולי מתאימים.`}
          </p>
          {item.receivedText && <p className="faint">{item.receivedText}</p>}
        </div>
        <div
          className="rail-ico tint-orange"
        >
          <IcMerge />
        </div>
      </div>

      {/* The records themselves. A name alone is not enough to tell two people
          apart — the phone and the email are what the processor tripped over. */}
      <div className="dup-list">
        {loading && ids.length > 0 ? (
          <p className="faint" style={{ margin: 0 }}>
            טוענים את הכרטיסים…
          </p>
        ) : candidates.length === 0 ? (
          <p className="faint" style={{ margin: 0 }}>
            לא מצאנו את הכרטיסים. אולי נמחקו או אוחדו מאז.
          </p>
        ) : (
          candidates.map((p) => (
            <Link key={p.id} to={`/people/${p.id}`} className="dup-row">
              <Avatar name={p.displayName ?? "ללא שם"} size={38} />
              <span className="grow">
                <span className="name-row">
                  {p.displayName ?? "ללא שם"}
                </span>
                {(p.phones ?? []).slice(0, 2).map((ph, i) => (
                  <span className="muted mono" key={`p${i}`} dir="ltr" style={{ display: "block", textAlign: "start" }}>
                    {ph.value ?? ph.normalized}
                  </span>
                ))}
                {(p.emails ?? []).slice(0, 1).map((em, i) => (
                  <span className="muted mono" key={`e${i}`} dir="ltr" style={{ display: "block", textAlign: "start" }}>
                    {em.value}
                  </span>
                ))}
              </span>
              <IcChevron size={16} />
            </Link>
          ))
        )}
      </div>

      <div className="ask">
        <p className="ask-q">זה אותו בן אדם?</p>
        {/*
          Says what the answer does, because it does less than the wording
          promises. `resolveInboxItem` records the decision and closes the item;
          it does not merge. Every other card here admits what is not built yet —
          the unattributed payment says so outright — and this one implied a
          merge that never happened.
        */}
        {/*
          Says what the answer does, because it does less than the wording
          promises: `resolveInboxItem` records the decision and closes the item,
          it does not merge.

          It used to send you to "כרטיס האדם" to do the merge by hand, and the
          person card said merging had not been built. Both were pointing at each
          other and neither had the button. It is on the Duplicates screen.
        */}
        <p className="faint" style={{ margin: "0 0 10px" }}>
          נרשום את התשובה ונסגור את הכרטיס, אבל לא נאחד.{" "}
          <Link to="/duplicates" style={{ textDecoration: "underline" }}>
            מאחדים במסך הכפילויות
          </Link>
          .
        </p>
        <div className="btn-row">
          <button
            className="btn btn-primary"
            disabled={busy}
            onClick={() => onAct(item, "same_person")}
          >
            כן, אותו בן אדם
          </button>
          <button className="btn" disabled={busy} onClick={() => onAct(item, "different_person")}>
            לא, שני אנשים שונים
          </button>
        </div>
      </div>
    </article>
  );
}

function EmptyInbox({ all }: { all: boolean }) {
  return (
    <div className="empty">
      <div className="empty-ico tint-orange">
        <IcInbox size={26} />
      </div>
      <h3>{all ? "האינבוקס ריק" : "אין פה כלום. נסו לשונית אחרת."}</h3>
      <p>
        טופס מהאתר, איש קשר חדש או מקרה שהמערכת לא בטוחה לגביו יופיעו כאן, עם כפתורים
        להחלטה. המערכת לא מנחשת במקומכם.
      </p>
    </div>
  );
}
